import type { ColumnDef } from '@tanstack/react-table'
import { CaretUp, CaretDown } from '@phosphor-icons/react'
import { Badge } from '@/components/ui/badge'
import { OrgRowActions } from './OrgRowActions'
import type { Organization } from '@/api/organizations'

function SortableHeader({
  label,
  column,
}: {
  label: string
  column: { getIsSorted: () => false | 'asc' | 'desc'; toggleSorting: (desc?: boolean) => void }
}) {
  const sorted = column.getIsSorted()
  return (
    <button
      type="button"
      className="flex items-center gap-1 hover:text-foreground transition-colors -ml-2 px-2 py-1 rounded"
      onClick={() => column.toggleSorting(sorted === 'asc')}
    >
      {label}
      {sorted === 'asc' && <CaretUp className="h-3.5 w-3.5" weight="bold" />}
      {sorted === 'desc' && <CaretDown className="h-3.5 w-3.5" weight="bold" />}
    </button>
  )
}

export const orgColumns: ColumnDef<Organization>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => <SortableHeader label="Organization" column={column} />,
    cell: ({ row }) => (
      <div>
        <div className="font-medium">{row.original.name}</div>
        <div className="text-xs text-muted-foreground">{row.original.slug}</div>
      </div>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'primary_vertical_slug',
    header: 'Vertical',
    cell: ({ getValue }) => {
      const value = getValue<string | null>()
      return value ? (
        <Badge variant="outline">{value}</Badge>
      ) : (
        <span className="text-muted-foreground text-xs">--</span>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: 'user_count',
    header: 'Users',
    cell: ({ getValue }) => (
      <span className="tabular-nums">{getValue<number>()}</span>
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'subscription_tier',
    header: 'Tier',
    cell: ({ getValue }) => (
      <Badge variant="secondary" className="capitalize">{getValue<string>()}</Badge>
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'is_active',
    header: ({ column }) => <SortableHeader label="Status" column={column} />,
    cell: ({ getValue }) => {
      const active = getValue<boolean>()
      return (
        <Badge variant={active ? 'default' : 'destructive'}>
          {active ? 'Active' : 'Inactive'}
        </Badge>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: 'created_at',
    header: ({ column }) => <SortableHeader label="Created" column={column} />,
    cell: ({ getValue }) => {
      const date = new Date(getValue<string>())
      return (
        <span className="text-muted-foreground text-sm">
          {date.toLocaleDateString('en-IN', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
      )
    },
    enableSorting: true,
  },
  {
    id: 'actions',
    cell: ({ row }) => <OrgRowActions org={row.original} />,
    enableSorting: false,
  },
]
